import { string, addMethod } from 'yup';
import { tnStrictRegex } from './tnStrictRegex';

/**
 * Adds a method to yup.string that strictly validates a TN.
 * The Yup method that gets created accepts the same optional parameters to override the defaults.
 *
 * @module
 *
 * @param {RegExp} [defaultRegex=/^[2-9]\d{2}[2-9]\d{2}\d{4}$/] The default regex used when the yup method is invoked with no regex argument provided
 * @param {string} [defaultMessage=({ path }) => `${path} must be a valid telephone number`] The default message used when the yup method is invoked with no message argument provided
 * @param {string} [validationName='tnStrict'] The name of the validation to add to Yup
 *
 * @example
 * import { addTnStrictToYup } from '@somosgov/js-util-lib';
 * import { string, object } from 'yup'
 *
 * // Add the method to Yup
 * addTnStrictToYup();
 *
 * // Using the method in a schema
 * const schema = object({
 *    // Default args
 *    tfn: string().tnStrict(),
 *    // Custom args
 *    tfn2: string().tnStrict(/^[2-9]\d{2}-[2-9]\d{2}-\d{4}$/, 'Must use the following format: 303-213-4000'),
 * })
 *
 * // Add with custom Name
 * addTnStrictToYup(
 *    /^[0-9a-z]{3}-?[0-9a-z]{3}-?[0-9a-z]{4}$/i,
 *    "Must be a valid Toll-Free number",
 *    'tollFree'
 * );
 *
 * // Using the named method in a schema
 * const schema = object({
 *    tfn: string().tollFree(),
 * })
 *
 */
const addTnStrictToYup = (
   defaultRegex = tnStrictRegex,
   defaultMessage = ({ path }) => `${path} must be a valid telephone number`,
   validationName = 'tnStrict'
) =>
   addMethod(string, validationName, function tn(
      regex = defaultRegex,
      message = defaultMessage
   ) {
      return this.matches(regex, message);
   });

export { addTnStrictToYup };
